import type { PaymentMode, PaymentRecordView, PublicOffer } from '@dunea/shared';
import httpClient from '~/lib/http-client';

// Owner-facing Payments plugin API (studio `PaymentsOwnerController`).
// `appId` is the CHAT id — the Payments plugin keys everything by app, not by
// deployment, so these calls work before the site is ever published.

export type PaymentsLiveState = 'test' | 'live' | 'paused';

export interface PaymentsSettings {
  appId: string;
  liveState: PaymentsLiveState;
  liveSince: string | null;
  pausedReason: string | null;
  currency: string;
  notifyEmail: string | null;
  providerOverrides: Record<string, string> | null;
}

export interface PaymentAccount {
  id: string;
  provider: string;
  mode: PaymentMode;
  source: 'connect' | 'byo' | 'dunea_sandbox' | 'manual';
  status: 'pending' | 'active' | 'restricted';
  fingerprint: string | null;
  /** Direct UPI accounts: the UPI ID payments go to and the name buyers see. */
  upi?: { vpa: string; payeeName: string };
}

export interface PaymentsChecklistItem {
  id: string;
  ok: boolean;
  blocking: boolean;
  message: string;
}

export interface PaymentsOverview {
  settings: PaymentsSettings;
  accounts: PaymentAccount[];
  checklist: PaymentsChecklistItem[];
  webhookUrls: { razorpay: Record<PaymentMode, string> };
  sandbox: { stripe: boolean; razorpay: boolean };
}

export interface PaymentListRow extends PaymentRecordView {
  accountId: string;
  providerPaymentRef: string | null;
  feeAmount: number;
  holdUntil: string | null;
  paidAt: string | null;
  fulfilledAt: string | null;
  lineCount: number;
  /** Direct UPI: the 12-digit UTR the buyer says they paid with. */
  buyerReference?: string | null;
  /** When the buyer's latest proof (UTR or screenshot) came in. */
  buyerReferenceAt?: string | null;
  /** Direct UPI: the buyer attached a payment screenshot (fetch it with apiGetPaymentProof). */
  hasScreenshot?: boolean;
  buyerProofType?: string | null;
  /** Not returned by the API yet; the list falls back to `paidAt`. */
  createdAt?: string | null;
}

export interface PaymentOffer {
  id: string;
  appId: string;
  slug: string;
  title: string;
  description: string | null;
  amount: number;
  currency: string;
  recurring: PublicOffer['recurring'];
  status: string;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface OfferInput {
  slug: string;
  title: string;
  description?: string;
  amount: number;
  currency: string;
  recurring?: PublicOffer['recurring'];
}

const base = (appId: string) => `/api/plugins/payments/apps/${encodeURIComponent(appId)}`;

export async function apiGetPaymentsOverview(appId: string): Promise<PaymentsOverview> {
  const { data } = await httpClient.get<PaymentsOverview>(base(appId));
  return data;
}

export async function apiUpdatePaymentsSettings(
  appId: string,
  patch: { currency?: string; notifyEmail?: string | null },
): Promise<PaymentsSettings> {
  const { data } = await httpClient.put<PaymentsSettings>(`${base(appId)}/settings`, patch);
  return data;
}

export async function apiSaveRazorpayKeys(
  appId: string,
  mode: PaymentMode,
  keys: { keyId: string; keySecret: string; webhookSecret: string },
): Promise<{ account: PaymentAccount; webhookUrl: string }> {
  const { data } = await httpClient.post<{ account: PaymentAccount; webhookUrl: string }>(
    `${base(appId)}/accounts/razorpay/${mode}`,
    keys,
  );
  return data;
}

export async function apiStartStripeConnect(
  appId: string,
  mode: PaymentMode,
  urls: { returnUrl: string; refreshUrl: string },
): Promise<{ url: string }> {
  const { data } = await httpClient.post<{ url: string }>(`${base(appId)}/accounts/stripe/${mode}/connect`, urls);
  return data;
}

export async function apiEnableCashOnDelivery(appId: string): Promise<PaymentAccount[]> {
  const { data } = await httpClient.post<PaymentAccount[]>(`${base(appId)}/accounts/cod`, {});
  return data;
}

export async function apiSaveUpiAccount(
  appId: string,
  input: { vpa: string; payeeName: string },
): Promise<PaymentAccount[]> {
  const { data } = await httpClient.post<PaymentAccount[]>(`${base(appId)}/accounts/upi`, input);
  return data;
}

export async function apiGoLive(appId: string): Promise<{ ok: boolean; checklist: PaymentsChecklistItem[] }> {
  const { data } = await httpClient.post<{ ok: boolean; checklist: PaymentsChecklistItem[] }>(
    `${base(appId)}/go-live`,
    {},
  );
  return data;
}

export async function apiPausePayments(appId: string, reason?: string): Promise<PaymentsSettings> {
  const { data } = await httpClient.post<PaymentsSettings>(`${base(appId)}/pause`, reason ? { reason } : {});
  return data;
}

export async function apiResumePayments(appId: string): Promise<{ ok: boolean; checklist: PaymentsChecklistItem[] }> {
  const { data } = await httpClient.post<{ ok: boolean; checklist: PaymentsChecklistItem[] }>(
    `${base(appId)}/resume`,
    {},
  );
  return data;
}

export async function apiListPayments(appId: string, mode?: PaymentMode): Promise<PaymentListRow[]> {
  const query = mode ? `?mode=${mode}` : '';
  const { data } = await httpClient.get<PaymentListRow[]>(`${base(appId)}/payments${query}`);

  return data;
}

export async function apiRefundPayment(appId: string, paymentId: string, amount?: number): Promise<void> {
  await httpClient.post(
    `${base(appId)}/payments/${encodeURIComponent(paymentId)}/refund`,
    amount === undefined ? {} : { amount },
  );
}

export async function apiConfirmPayment(appId: string, paymentId: string): Promise<void> {
  await httpClient.post(`${base(appId)}/payments/${encodeURIComponent(paymentId)}/confirm`, {});
}

/** The buyer's payment screenshot (private; behind the owner's login, so fetched as a blob). */
export async function apiGetPaymentProof(appId: string, paymentId: string): Promise<Blob> {
  const { data } = await httpClient.get<Blob>(`${base(appId)}/payments/${encodeURIComponent(paymentId)}/proof`, {
    responseType: 'blob',
  });
  return data;
}

/** The cash never came, or no UPI transfer matches the buyer's UTR: cancels the order. */
export async function apiRejectPayment(appId: string, paymentId: string): Promise<void> {
  await httpClient.post(`${base(appId)}/payments/${encodeURIComponent(paymentId)}/reject`, {});
}

export async function apiListOffers(appId: string): Promise<PaymentOffer[]> {
  const { data } = await httpClient.get<PaymentOffer[]>(`${base(appId)}/offers`);
  return data;
}

export async function apiUpsertOffer(appId: string, offer: OfferInput): Promise<PaymentOffer> {
  const { data } = await httpClient.post<PaymentOffer>(`${base(appId)}/offers`, offer);
  return data;
}
